// Game types that are set up like Spin The Wheel (same forms, steps, data and preview)
export const SPIN_THE_WHEEL_LIKE_GAMES = [
  "spin_the_wheel",
  "wordle",
  "pop_ninja",
  "shopping_jackpot",
  "screenshot_challenge",
  "trivia",
];

// Game types that can be selected when creating a game; anything else shows "Coming Soon"
export const ENABLED_GAMES = ["scratch_card", ...SPIN_THE_WHEEL_LIKE_GAMES];

export const isSpinTheWheelLike = (game_type_code) =>
  SPIN_THE_WHEEL_LIKE_GAMES.includes(game_type_code);

// The thumbnail on the game list ("Card Thumbnail" in Before Play, returned as icon_url on unplayed cards).
// Each starts as the game type's own icon; game_thumbnails.sql adds the same image as the field's preset.
const SOULLAB_ICONS = "https://cdn-api.soullab.dev.esmagico.in/soullab-assets";
export const GAME_THUMBNAILS = {
  wordle: `${SOULLAB_ICONS}/c606e677-cc0f-44b2-a41b-3719dc2d806a.png`,
  pop_ninja: `${SOULLAB_ICONS}/9b459bd8-649b-432d-bd34-d34c646657d5.png`,
  shopping_jackpot: `${SOULLAB_ICONS}/2a799fc5-f177-450d-8883-ac7927b9af09.png`,
  screenshot_challenge: `${SOULLAB_ICONS}/6f82b249-a3d8-459d-afb9-c75817897342.png`,
  trivia: "https://gratifystorage01.blob.core.windows.net/gratify-dev/9fa732_STW_ICON_COL.png",
};

// Promo Code reward (reward type "voucher") on the new games. Its codes come from one of two sources, and it uses
// the game's normal reward sheet with the code on the card (see PROMO_SCOPE.md). Scratch Card and Spin the Wheel keep their own voucher design.
export const PROMO_REWARD = "voucher";
export const PROMO_SOURCE = { FETCH: "FETCH", GENERATED: "GENERATED" };
export const PROMO_PREVIEW_CODE = "FLY7KQ2MXP";
export const promoSourceOf = (reward) => reward?.promo_source?.id ?? reward?.promo_source;
export const promoRewardAfterPlayDefaults = (defaults) => ({
  ...defaults,
  title: "Your promo code",
  description: "Use this code at checkout on your next booking.",
  start_shopping_cta_text: "Use it now",
  copy_button_text: "Copy",
  code_loading_text: "Getting your code...",
  code_error_text: "Couldn't load your code",
  code_retry_text: "Try again",
});

// Shopping Jackpot: a slot machine. Uses the Spin The Wheel steps and forms, but each reward carries a
// symbol image instead of a spoke, there is no quiz, and the preview shows reels instead of a wheel.
export const SHOPPING_JACKPOT = "shopping_jackpot";

export const isShoppingJackpot = (game_type_code) =>
  game_type_code === SHOPPING_JACKPOT;

// Trivia: 3 typed-answer clues on a Rive board; the Quiz step becomes the Questions step
export const TRIVIA = "trivia";
export const TRIVIA_QUESTIONS = 3;
export const isTrivia = (game_type_code) => game_type_code === TRIVIA;
// A trivia game type is usable only once its config describes the questions (trivia_setup.sql); older rows stay "Coming Soon"
export const isTriviaConfigured = (game) => !isTrivia(game?.game_type_code) || !!game?.game_fields?.questions;

// Screenshot Challenge ("Stop the cab"): one snap per card. The app judges the snap and sends WIN / LOSE;
// a WIN pays one of the winning rewards (probability = share among winners), a LOSE pays Better Luck Next Time.
export const SCREENSHOT_CHALLENGE = "screenshot_challenge";
export const isScreenshotChallenge = (game_type_code) => game_type_code === SCREENSHOT_CHALLENGE;
// The cab's width on the track, in % (the SDK's CAB_W). Fixed: difficulty is the zone width and the crossing time.
export const SCREENSHOT_OBJECT_WIDTH = 22;
export const SCREENSHOT_MAX_WINNING_REWARDS = 3;
// The panel shows plain choices; it saves them together with the numbers the app reads (all % of the track / seconds):
//   Box Position (left / centre / right) + Box Size (small / medium / large) -> zone_start, zone_width
//   Cab Speed 1-5 (1 = slowest)                                              -> crossing_time
// The SDK's look is right / small / speed 4 (box 56..82, about 1 s per crossing).
export const SCREENSHOT_BOX_POSITIONS = { left: 31, centre: 50, right: 69 }; // where the box's middle sits
export const SCREENSHOT_BOX_SIZES = { small: 26, medium: 32, large: 40 };
export const SCREENSHOT_SPEEDS = { 1: "5", 2: "3", 3: "2", 4: "1", 5: "0.5" };
export const SCREENSHOT_SPEED_NAMES = { 1: "Very slow", 2: "Slow", 3: "Medium", 4: "Fast", 5: "Very fast" };
export const SCREENSHOT_LAYOUT_DEFAULTS = { zone_position: "right", box_size: "small", speed_level: "4" };
const choice = (v) => v?.id ?? v;
// The app-facing numbers for the panel's choices (missing choices fall back to the SDK look)
export const screenshotLayoutFields = (beforePlay = {}) => {
  const position = SCREENSHOT_BOX_POSITIONS[choice(beforePlay.zone_position)] ?? SCREENSHOT_BOX_POSITIONS.right;
  const width = SCREENSHOT_BOX_SIZES[choice(beforePlay.box_size)] ?? SCREENSHOT_BOX_SIZES.small;
  const start = Math.round(Math.min(100 - width, Math.max(0, position - width / 2)));
  return {
    zone_start: String(start),
    zone_width: String(width),
    crossing_time: SCREENSHOT_SPEEDS[choice(beforePlay.speed_level)] ?? SCREENSHOT_SPEEDS[4],
  };
};
export const SCREENSHOT_LAYOUT_FIELDS = ["zone_position", "box_size", "speed_level"];
// The track is always an image (presets below). A cab image replaces the drawn cab, so its colour is hidden then.
export const SCREENSHOT_TRACK_PRESETS = {
  airport: "https://cdn-api.soullab.dev.esmagico.in/soullab-assets/e1bc950a-f225-47b7-97cd-bcc0e0cf5a08.png",
  coast: "https://cdn-api.soullab.dev.esmagico.in/soullab-assets/22ed8a8d-27ef-4941-90a9-362698f31789.png",
};
export const SCREENSHOT_IMAGE_REPLACES = {
  object_image: ["object_color"],
};
export const screenshotHidesField = (backendLabel, beforePlay) =>
  Object.entries(SCREENSHOT_IMAGE_REPLACES).some(
    ([image, fields]) => fields.includes(backendLabel) && typeof beforePlay?.[image] === "string" && beforePlay[image].trim() !== ""
  );
export const screenshotSpeedHint = (level) => {
  const seconds = SCREENSHOT_SPEEDS[choice(level)];
  return seconds ? `${SCREENSHOT_SPEED_NAMES[choice(level)]}: the cab crosses the road in ${seconds} ${seconds === "1" ? "second" : "seconds"}` : "";
};
// What a reward pays on, for headers: BLNT on a miss; a winning reward on a win, split among the winning rewards
export const screenshotRewardNote = (rewards, index) => {
  const list = Array.isArray(rewards) ? rewards : [];
  const reward = list[index];
  if (reward?.reward_type?.id === "bl_next_time") return "paid on a miss";
  const winners = list.filter((r) => r?.reward_type?.id && r.reward_type.id !== "bl_next_time");
  if (winners.length <= 1) return "paid on every win";
  const total = winners.reduce((sum, r) => sum + (parseFloat(r?.win_probability) || 0), 0);
  const share = total > 0 ? ((parseFloat(reward?.win_probability) || 0) * 100) / total : 100 / winners.length;
  return `${Math.round(share * 100) / 100}% of wins`;
};

// Only wheel-like games with a quiz get the "Quiz" step between Before Play and After Play
export const hasQuizStep = (game_type_code) =>
  isSpinTheWheelLike(game_type_code) && !isShoppingJackpot(game_type_code) && !isScreenshotChallenge(game_type_code);
// The name of that step: Trivia's is Questions, Pop Ninja's is Clouds (game_type_code is compared as a string,
// since Pop Ninja's constant is declared further down)
export const quizStepName = (game_type_code) =>
  game_type_code === "trivia" ? "Questions" : game_type_code === "pop_ninja" ? "Clouds" : game_type_code === "wordle" ? "Words" : "Quiz";

// Tagline shown on the reward-sheet previews (they were written for the wheel)
export const gameTagline = (game_type_code) =>
  isShoppingJackpot(game_type_code) ? "Jackpot!" : "Spin the wheel to win!";

// The machine: always 3 reels showing the game's 3 symbols. Each winning reward is mapped to one symbol
// (its "symbol" dropdown, { id: "1" | "2" | "3" }); three of that symbol pays the reward. Unmapped symbols never win.
export const JACKPOT_REELS = 3;
export const JACKPOT_SYMBOLS = 3;
export const ANY_SYMBOL = "any";
export const JACKPOT_SYMBOL_OPTIONS = [
  { id: "1", name: "Symbol 1" },
  { id: "2", name: "Symbol 2" },
  { id: "3", name: "Symbol 3" },
  { id: ANY_SYMBOL, name: "Any symbol (three of any)" },
];

// 1..3, "any", or null when unset
export const rewardSymbolChoice = (rewardDesign) => {
  let v = rewardDesign?.symbol;
  if (v && typeof v === "object") v = v.id;
  if (String(v ?? "").trim().toLowerCase() === ANY_SYMBOL) return ANY_SYMBOL;
  const n = parseInt(v, 10);
  return n >= 1 && n <= JACKPOT_SYMBOLS ? n : null;
};
export const rewardSymbolNumber = (rewardDesign) => {
  const c = rewardSymbolChoice(rewardDesign);
  return typeof c === "number" ? c : null;
};

// The game's symbols in order: { number, image, label }
export const jackpotSymbols = (beforePlay) =>
  [1, 2, 3]
    .map((n) => ({ number: n, image: beforePlay?.[`symbol_${n}`], label: beforePlay?.[`symbol_${n}_label`] }))
    .filter((s) => typeof s.image === "string" && s.image.trim());

// Shopping Jackpot defaults, copied from gratify-sdk (components/games/ShoppingJackpot.tsx, shared.tsx,
// lib/offers.ts) with the "theme-departures" tokens from styles.css converted to hex.
export const JACKPOT_SDK_THEME = {
  bg: "#060606",        // --g-bg oklch(0.12 0 0)
  surface: "#0F0F0F",   // --g-surface oklch(0.17 0 0)
  fg: "#F1EEE7",        // --g-fg oklch(0.95 0.01 90)
  muted: "#74716B",     // --g-muted oklch(0.55 0.01 90)
  accent: "#FAC131",    // --g-accent oklch(0.84 0.16 85)
  accentFg: "#060606",  // --g-accent-fg
};

// Game-level before play (BeforePlayPage) for a new jackpot game
// Default symbol / icon images (icons8 colour set), matching the SDK's Gift / Bag / Deal reels
export const JACKPOT_SDK_ICONS = {
  gift: "https://img.icons8.com/color/96/gift.png",
  bag: "https://img.icons8.com/color/96/shopping-bag.png",
  deal: "https://img.icons8.com/color/96/price-tag.png",
  coins: "https://img.icons8.com/color/96/coins.png",
};

export const JACKPOT_SDK_BACKGROUND =
  "https://images.stockcake.com/public/a/1/e/a1e5307f-e454-4702-9070-c4eee0665430_large/plain-black-background-stockcake.jpg";

export const jackpotBeforePlayDefaults = () => ({
  thumbnail_image: GAME_THUMBNAILS.shopping_jackpot,
  title: "Shopping jackpot",
  subtitle: "Match three gifts to unlock ₹500 off.",
  title_color: JACKPOT_SDK_THEME.fg,
  background_color: JACKPOT_SDK_THEME.bg,
  background_image: JACKPOT_SDK_BACKGROUND,
  pull_button_text: "Play now",
  pull_button_color: JACKPOT_SDK_THEME.accent,
  symbol_1: JACKPOT_SDK_ICONS.gift,
  symbol_1_label: "Gift",
  symbol_2: JACKPOT_SDK_ICONS.bag,
  symbol_2_label: "Bag",
  symbol_3: JACKPOT_SDK_ICONS.deal,
  symbol_3_label: "Deal",
  read_instructions:
    "<p>Today’s jackpot</p><ol><li>Tap <strong>Play now</strong> to spin the reels.</li><li>The reels stop one by one, left to right.</li><li>Gift, gift, gift wins the jackpot.</li><li>Two matched is a near miss, better luck next time.</li></ol>",
  terms_and_conditions:
    "<p>Terms &amp; conditions</p><ol><li>Minimum order value ₹2500.</li><li>One use per customer.</li><li>Cannot be combined with other coupons.</li><li>Brand reserves the right to modify or withdraw the offer.</li></ol>",
});

// Per-reward before play (BeforePlayPage2by2): which of the game's 3 symbols pays this reward.
// A single winning reward pays on any symbol (nothing to map); with several, they default to Symbol 1, 2, 3 in order.
export const ANY_SYMBOL_OPTION = JACKPOT_SYMBOL_OPTIONS.find((o) => o.id === ANY_SYMBOL);
export const jackpotRewardBeforePlayDefaults = (reward, index = 0, winningCount = 1) =>
  reward?.id === "bl_next_time"
    ? { before_play_reward: reward }
    : {
        before_play_reward: reward,
        symbol: winningCount <= 1 ? ANY_SYMBOL_OPTION : JACKPOT_SYMBOL_OPTIONS[Math.min(index, JACKPOT_SYMBOLS - 1)],
      };

// Per-reward after play (AfterPlayPage): the SDK's Reward bottom sheet
export const jackpotRewardAfterPlayDefaults = (reward) => {
  const t = JACKPOT_SDK_THEME;
  if (reward?.id === "bl_next_time") {
    // SDK miss state, shown on the machine: "So close" + one line
    return {
      title: "So close",
      title_colour: t.fg,
      description: "Two matched — better luck next time.",
      description_color: t.muted,
    };
  }
  // SDK Reward bottom sheet (coins variant); every colour and text has a field
  return {
    sheet_background_color: t.surface,
    context_text: "Jackpot! Three gifts matched",
    context_color: t.accent,
    logo_image: JACKPOT_SDK_ICONS.coins,
    card_background_color: t.accent,
    card_label_color: t.accentFg,
    reward_value_color: t.accentFg,
    title: "Flat ₹500 off",
    title_colour: t.fg,
    description: "Apply the code at checkout on the marketplace.",
    description_color: t.muted,
    validity_text: "Valid for 7 days",
    pill_background_color: t.bg,
    pill_text_color: t.muted,
    tnc_text: "Terms & conditions",
    tnc_color: t.muted,
    show_cta: true,
    start_shopping_cta_text: "Apply on marketplace",
    start_shopping_link: "https://www.goindigo.in/",
    button_background_color: t.accent,
    button_text_color: t.accentFg,
    go_back_cta_text: "Saved to My Rewards · Play again",
    go_back_color: t.muted,
  };
};


// ---------- Trivia defaults, copied from gratify-sdk (components/games/CodeBreaker.tsx) with "theme-code" in hex
export const TRIVIA_SDK_THEME = {
  bg: "#F7EDE0",       // --g-bg oklch(0.95 0.02 75)
  surface: "#FFFBF4",  // --g-surface
  fg: "#321B13",       // --g-fg
  muted: "#856B5D",    // --g-muted
  accent: "#B94739",   // --g-accent
  accentFg: "#FCF8F1", // --g-accent-fg
  line: "#DBCBB9",
};

export const triviaBeforePlayDefaults = () => ({
  thumbnail_image: GAME_THUMBNAILS.trivia,
  title: "Trivia",
  clue_label: "Clue",
  title_color: TRIVIA_SDK_THEME.fg,
  background_color: TRIVIA_SDK_THEME.bg,
  board_image: "https://img.icons8.com/color/480/world-map.png",
  board_caption: "Passport map",
  answer_length: "3",
  check_button_text: "Check code",
  check_button_color: TRIVIA_SDK_THEME.fg,
  wrong_title: "Not quite · fun fact",
  wrong_card_color: TRIVIA_SDK_THEME.accent,
  retry_text: "Try again",
  read_instructions:
    "<p>How to play</p><ol><li>Read the clue and type the answer in the boxes.</li><li>Tap <strong>Check code</strong>.</li><li>Wrong? Read the fun fact and try again, as many times as you like.</li><li>Solve all three to win.</li></ol>",
  terms_and_conditions:
    "<p>Terms &amp; conditions</p><ol><li>One trivia round per card.</li><li>The reward is credited after all three clues are solved.</li><li>Brand reserves the right to modify or withdraw the offer.</li></ol>",
});

// The SDK's three clues (airport codes)
export const triviaQuestionsDefaults = () => [
  { clue: "Pink City, palaces, 1h 30m from the capital.", answer: "JAI", hint: "J__", fun_fact: "Its old city walls are painted a terracotta pink." },
  { clue: "Sun, sand and Portuguese-era churches.", answer: "GOI", hint: "G__", fun_fact: "Home to over 40 beaches along a 100 km coast." },
  { clue: "Houseboats on a lake ringed by mountains.", answer: "SXR", hint: "S__", fun_fact: "Shikaras on Dal Lake are hand-carved cedar." },
];

// Per-reward after play: the SDK reward sheet in the trivia theme
export const triviaRewardAfterPlayDefaults = (reward) => {
  const t = TRIVIA_SDK_THEME;
  if (reward?.id === "bl_next_time") {
    return { title: "So close", title_colour: t.fg, description: "Three of three, but no prize this time. Try the next card.", description_color: t.muted };
  }
  return {
    sheet_background_color: t.surface,
    context_text: "3 of 3 correct — trivia champ",
    context_color: t.accent,
    logo_image: JACKPOT_SDK_ICONS.coins,
    card_background_color: t.accent,
    card_label_color: t.accentFg,
    reward_value_color: t.accentFg,
    title: `${reward?.name || "Reward"} credited`,
    title_colour: t.fg,
    description: `Added to your ${reward?.name || "reward"} wallet. Use it on your next booking.`,
    description_color: t.muted,
    validity_text: "Expires in 30 days",
    pill_background_color: t.bg,
    pill_text_color: t.muted,
    tnc_text: "Terms & conditions",
    tnc_color: t.muted,
    show_cta: true,
    start_shopping_cta_text: "Shop with it now",
    start_shopping_link: "https://www.goindigo.in/",
    button_background_color: t.accent,
    button_text_color: t.accentFg,
    go_back_cta_text: "Saved to My Rewards · Play again",
    go_back_color: t.muted,
  };
};


// ---------- Screenshot Challenge defaults, copied from gratify-sdk (components/games/StopTheCab.tsx) with "theme-cab" in hex
export const SCREENSHOT_SDK_THEME = {
  bg: "#0F171F",       // --g-bg oklch(0.2 0.02 250)
  surface: "#1D252D",  // --g-surface oklch(0.26 0.02 250)
  fg: "#F8F5EE",       // --g-fg oklch(0.97 0.01 90)
  muted: "#95A0AB",    // --g-muted oklch(0.7 0.02 250)
  accent: "#F9C718",   // --g-accent oklch(0.85 0.17 90)
  accentFg: "#0F171F", // --g-accent-fg oklch(0.2 0.02 250)
  alt: "#37BB62",      // --g-alt oklch(0.7 0.17 150)
  road: "#2F3338",     // --road oklch(0.32 0.01 250)
};

// Game-level before play (BeforePlayPage). The track starts as the airport preset; the cab image is empty, so the
// app draws the SDK cab in object_color.
export const screenshotBeforePlayDefaults = () => ({
  thumbnail_image: GAME_THUMBNAILS.screenshot_challenge,
  title: "Stop the cab",
  subtitle: "Snap the screenshot when the cab is fully inside the parking box.",
  title_color: SCREENSHOT_SDK_THEME.fg,
  subtitle_color: SCREENSHOT_SDK_THEME.muted,
  background_color: SCREENSHOT_SDK_THEME.bg,
  background_image: SCREENSHOT_TRACK_PRESETS.airport,
  show_track_dashes: "yes",
  object_image: "",
  object_color: SCREENSHOT_SDK_THEME.accent,
  ...SCREENSHOT_LAYOUT_DEFAULTS,
  ...screenshotLayoutFields(SCREENSHOT_LAYOUT_DEFAULTS),
  zone_label: "Stop here",
  zone_color: SCREENSHOT_SDK_THEME.accent,
  start_button_text: "Start the cab",
  snap_button_text: "Snap!",
  button_color: SCREENSHOT_SDK_THEME.accent,
  button_text_color: SCREENSHOT_SDK_THEME.accentFg,
  idle_text: "Win a cab voucher for your next ride.",
  miss_title: "So close!",
  miss_text: "Off by {n}% — come back tomorrow.",
  read_instructions:
    "<p>How to play</p><ol><li>Tap <strong>Start the cab</strong>.</li><li>Tap <strong>Snap!</strong> when the cab is fully inside the parking box.</li><li>You get one snap per card.</li><li>A perfect stop wins a reward.</li></ol>",
  terms_and_conditions:
    "<p>Terms &amp; conditions</p><ol><li>One snap per card.</li><li>The reward is credited after a perfect stop.</li><li>Brand reserves the right to modify or withdraw the offer.</li></ol>",
});

// Per-reward after play: the SDK reward sheet in the cab theme; Better Luck Next Time is the miss line on the game
export const screenshotRewardAfterPlayDefaults = (reward) => {
  const t = SCREENSHOT_SDK_THEME;
  if (reward?.id === "bl_next_time") {
    return { title: "So close!", title_colour: t.accent, description: "Missed the box this time. Come back tomorrow.", description_color: t.muted };
  }
  return {
    sheet_background_color: t.surface,
    context_text: "Perfect parking! Screenshot captured",
    context_color: t.accent,
    logo_image: JACKPOT_SDK_ICONS.coins,
    card_background_color: t.accent,
    card_label_color: t.accentFg,
    reward_value_color: t.accentFg,
    title: `${reward?.name || "Reward"} credited`,
    title_colour: t.fg,
    description: "Perfect stop! Use it on your next booking.",
    description_color: t.muted,
    validity_text: "Expires in 30 days",
    pill_background_color: t.bg,
    pill_text_color: t.muted,
    tnc_text: "Terms & conditions",
    tnc_color: t.muted,
    show_cta: true,
    start_shopping_cta_text: "Use it now",
    start_shopping_link: "https://www.goindigo.in/",
    button_background_color: t.accent,
    button_text_color: t.accentFg,
    go_back_cta_text: "Saved to My Rewards",
    go_back_color: t.muted,
  };
};

// Where the app stops the cab and whether that is a win (mirrors StopTheCab.tsx; used by the previews)
export const screenshotZone = (beforePlay) => {
  const width = Math.min(100, Math.max(SCREENSHOT_OBJECT_WIDTH + 1, parseFloat(beforePlay?.zone_width) || 26));
  const start = Math.min(100 - width, Math.max(0, parseFloat(beforePlay?.zone_start) || 56));
  return { start, width, end: start + width };
};
export const screenshotOffBy = (zone, position) =>
  Math.max(1, Math.round(position < zone.start ? zone.start - position : position + SCREENSHOT_OBJECT_WIDTH - zone.end));

// ---------- Pop Ninja ("PopNinja" in the SDK: components/games/CloudPop.tsx, theme "theme-cloud" in hex).
// The server deals each round and scores the pops (see POP_NINJA_SCOPE.md); the admin sets the look, the pace, the cloud
// kinds (Clouds step, saved as game.details.clouds) and a Minimum Score per winning reward (the tiers).
export const POP_NINJA = "pop_ninja";
export const isPopNinja = (game_type_code) => game_type_code === POP_NINJA;
export const POP_NINJA_MAX_CLOUDS = 6;
export const POP_NINJA_MAX_WINNING_REWARDS = 3;

export const POP_NINJA_SDK_THEME = {
  bg: "#A9D9F1",       // --g-bg oklch(0.86 0.06 230)
  surface: "#EBF7FD",  // --g-surface oklch(0.97 0.015 230)
  fg: "#0B223E",       // --g-fg oklch(0.25 0.06 255)
  muted: "#405870",    // --g-muted oklch(0.45 0.05 250)
  accent: "#F0BB3B",   // --g-accent oklch(0.82 0.15 85)
  alt: "#D179CA",      // --g-alt oklch(0.7 0.15 330)
};

const SOULLAB = "https://cdn-api.soullab.dev.esmagico.in/soullab-assets";
export const POP_NINJA_SKY_PRESETS = {
  day: `${SOULLAB}/d2b690ad-0f5e-4bb6-a898-0cb0fd3a42ed.png`,
  sunset: `${SOULLAB}/36c29aee-7ecd-455f-8ff3-390e2bb63b72.png`,
  night: `${SOULLAB}/e8397ec0-8482-4806-8596-f151808813f2.png`,
};
export const POP_NINJA_CLOUD_PRESETS = {
  normal: `${SOULLAB}/18c3810d-a9e8-47be-93a2-721c85508896.png`,
  gold: `${SOULLAB}/35226ce8-3636-46d5-a731-d4e6caacd0d1.png`,
  rainbow: `${SOULLAB}/bd3ded76-74bd-400f-a25e-ee3fa51d8219.png`,
  storm: `${SOULLAB}/0c9733b1-9f36-46b7-940f-c1149635aef1.png`,
};

// Difficulty sets only how fast clouds rise; every level gets the same clouds (one every 450 ms, as the SDK).
// The server deals rounds from the same table (pop-ninja.utils.ts POP_NINJA_PACE): keep the two in step.
export const POP_NINJA_PACE = {
  1: { name: "Very easy", spawn_ms: 450, rise_min_ms: 7000, rise_max_ms: 9000 },
  2: { name: "Easy", spawn_ms: 450, rise_min_ms: 5500, rise_max_ms: 7500 },
  3: { name: "Medium", spawn_ms: 450, rise_min_ms: 4000, rise_max_ms: 7000 },
  4: { name: "Hard", spawn_ms: 450, rise_min_ms: 3000, rise_max_ms: 4500 },
  5: { name: "Very hard", spawn_ms: 450, rise_min_ms: 2000, rise_max_ms: 3000 },
};
export const popNinjaPace = (beforePlay) => {
  const level = beforePlay?.difficulty?.id ?? beforePlay?.difficulty;
  const pace = POP_NINJA_PACE[level] || POP_NINJA_PACE[3];
  const seconds = parseInt(beforePlay?.round_seconds?.id ?? beforePlay?.round_seconds, 10);
  return { ...pace, duration_ms: ([15, 20, 30, 45, 60].includes(seconds) ? seconds : 20) * 1000 };
};
export const popNinjaPaceHint = (level) => {
  const pace = POP_NINJA_PACE[level?.id ?? level];
  return pace ? `${pace.name}: clouds take ${pace.rise_min_ms / 1000}-${pace.rise_max_ms / 1000}s to cross the screen` : "";
};

export const POP_NINJA_EFFECTS = [
  { id: "add", name: "Add points" },
  { id: "multiply", name: "Multiply (rest of round)" },
  { id: "lose", name: "Lose points" },
  { id: "end", name: "End the round" },
];

// The SDK's four clouds (CloudPop.tsx): 75 / 10 / 5 / 10
export const popNinjaCloudsDefaults = () => [
  { name: "Cloud", image: POP_NINJA_CLOUD_PRESETS.normal, effect: "add", value: "10", chance: "75" },
  { name: "Gold", image: POP_NINJA_CLOUD_PRESETS.gold, effect: "add", value: "50", chance: "10" },
  { name: "Rainbow", image: POP_NINJA_CLOUD_PRESETS.rainbow, effect: "multiply", value: "2", chance: "5" },
  { name: "Storm", image: POP_NINJA_CLOUD_PRESETS.storm, effect: "end", value: "", chance: "10" },
];

export const popNinjaBeforePlayDefaults = () => ({
  thumbnail_image: GAME_THUMBNAILS.pop_ninja,
  title: "PopNinja",
  subtitle: "Swipe through clouds. Gold = points, pink = 2x. Avoid the storm.",
  tagline: "Cruising altitude · 1,840",
  title_color: POP_NINJA_SDK_THEME.fg,
  subtitle_color: POP_NINJA_SDK_THEME.muted,
  background_image: POP_NINJA_SKY_PRESETS.day,
  difficulty: "3",
  round_seconds: "20",
  start_button_text: "Take off",
  start_button_color: POP_NINJA_SDK_THEME.fg,
  start_button_text_color: POP_NINJA_SDK_THEME.surface,
  score_color: POP_NINJA_SDK_THEME.fg,
  combo_color: POP_NINJA_SDK_THEME.accent,
  pop_effect: "✈",
  game_over_title: "Out of the sky!",
  game_over_text: "You scored {score}. Reach {next} for {reward}.",
  round_ended_text: "Storm! Round over.",
  read_instructions:
    "<p>How to play</p><ol><li>Tap <strong>Take off</strong>.</li><li>Tap or swipe through the clouds as they rise.</li><li>Gold clouds give more points, the pink cloud doubles your points.</li><li>Avoid the storm cloud: it ends the round.</li><li>The higher your score, the better the reward.</li></ol>",
  terms_and_conditions:
    "<p>Terms &amp; conditions</p><ol><li>One round per card.</li><li>The reward depends on the score at the end of the round.</li><li>Brand reserves the right to modify or withdraw the offer.</li></ol>",
});

// Minimum Score defaults by how many winning rewards there are (spread over a good 20s round at difficulty 3)
const POP_NINJA_TIER_DEFAULTS = { 1: ["200"], 2: ["150", "400"], 3: ["100", "300", "600"] };
export const popNinjaRewardBeforePlayDefaults = (reward, winningIndex = 0, winningCount = 1) =>
  reward?.id === "bl_next_time"
    ? { before_play_reward: reward }
    : { before_play_reward: reward, min_score: (POP_NINJA_TIER_DEFAULTS[Math.min(3, Math.max(1, winningCount))] || [])[winningIndex] ?? "" };

export const popNinjaRewardAfterPlayDefaults = (reward) => {
  const t = POP_NINJA_SDK_THEME;
  if (reward?.id === "bl_next_time") {
    return { title: "Out of the sky!", title_colour: t.fg, description: "You scored {score}. Reach {next} for {reward}.", description_color: t.muted };
  }
  return {
    sheet_background_color: t.surface,
    context_text: "You popped {score} — nice slicing!",
    context_color: t.fg,
    logo_image: JACKPOT_SDK_ICONS.coins,
    card_background_color: t.accent,
    card_label_color: t.fg,
    reward_value_color: t.fg,
    title: `${reward?.name || "Reward"} credited`,
    title_colour: t.fg,
    description: "Great flying! Use it on your next booking.",
    description_color: t.muted,
    validity_text: "Expires in 30 days",
    pill_background_color: t.bg,
    pill_text_color: t.fg,
    tnc_text: "Terms & conditions",
    tnc_color: t.muted,
    show_cta: true,
    start_shopping_cta_text: "Use it now",
    start_shopping_link: "https://www.goindigo.in/",
    button_background_color: t.fg,
    button_text_color: t.surface,
    go_back_cta_text: "Saved to My Rewards",
    go_back_color: t.muted,
  };
};

// The winning rewards' tiers, lowest first: [{ index, min_score, tier }]
export const popNinjaTiers = (rewards, rewardDesigns) =>
  (Array.isArray(rewards) ? rewards : [])
    .map((r, index) => ({ index, reward: r, min_score: parseFloat(rewardDesigns?.[index]?.min_score) }))
    .filter((t) => t.reward?.reward_type?.id && t.reward.reward_type.id !== "bl_next_time" && Number.isFinite(t.min_score))
    .sort((a, b) => a.min_score - b.min_score)
    .map((t, i) => ({ ...t, tier: i + 1 }));

// What a perfect player scores in a typical round: pops every cloud, mean mix (no storms if they end the round).
// Shown next to Minimum Score so a tier isn't set out of reach; the real score of a strong player is lower.
export const popNinjaStrongScore = (beforePlay, clouds) => {
  const { spawn_ms, duration_ms } = popNinjaPace(beforePlay);
  const list = (Array.isArray(clouds) ? clouds : []).map((c) => ({ effect: c?.effect?.id ?? c?.effect, value: parseFloat(c?.value) || 0, chance: parseFloat(c?.chance) || 0 }));
  const total = list.reduce((s, c) => s + c.chance, 0) || 1;
  const count = Math.ceil(duration_ms / spawn_ms);
  const addPoints = list.filter((c) => c.effect === "add").reduce((s, c) => s + (c.value * c.chance) / total, 0);
  const multiplier = list.some((c) => c.effect === "multiply" && c.chance > 0) ? Math.max(1, ...list.filter((c) => c.effect === "multiply").map((c) => c.value || 1)) : 1;
  // combo bonus averages about x2.6 over a long round (x1 for 5 pops, x2 for 5, then x3)
  const comboAverage = count <= 5 ? 1 : count <= 10 ? 1.5 : (5 + 10 + 3 * (count - 10)) / count;
  return Math.round((count * addPoints * comboAverage * (1 + multiplier) / 2) / 10) * 10;
};

// ---------- Wordle ("Wordle" in the SDK: components/games/Daily5.tsx, theme "theme-daily" in hex).
// One card a day, the same word for everyone, a streak ladder (see WORDLE_SCOPE.md). The admin sets the look, the answers
// (Words step, saved as game.details.words) and one winning reward, whose value is the Day 1 amount. The server checks every guess.
export const WORDLE = "wordle";
export const isWordle = (game_type_code) => game_type_code === WORDLE;
export const WORDLE_LENGTH = 5;
export const WORDLE_MIN_WORDS = 30;
export const WORDLE_MAX_WORDS = 5000;
export const WORDLE_STREAK_CYCLE = 7;
export const WORDLE_OFFER_MODE = "FRONTEND_MODE";

export const WORDLE_SDK_THEME = {
  bg: "#F9F5EC",       // --g-bg oklch(0.97 0.012 85)
  surface: "#FFFFFF",  // --g-surface oklch(1 0 0)
  fg: "#121B29",       // --g-fg oklch(0.22 0.03 260)
  muted: "#6B727E",    // --g-muted oklch(0.55 0.02 260)
  accent: "#1043A8",   // --g-accent oklch(0.42 0.17 262)
  accentFg: "#F8F8F8", // --g-accent-fg oklch(0.98 0 0)
  alt: "#65BAE1",      // --g-alt oklch(0.75 0.1 230)
  line: "#DCD7CD",     // --g-line oklch(0.88 0.015 85)
};

export const WORDLE_DEFAULT_WORDS = [
  "CLOUD", "PILOT", "PLANE", "CABIN", "DELAY", "AISLE", "RADAR", "CARGO", "TRAIN", "HOTEL",
  "BEACH", "COAST", "OCEAN", "TOWER", "FLYER", "SKIES", "WINGS", "GLIDE", "SOLAR", "ROUTE",
  "TRIPS", "VISAS", "TOURS", "GUIDE", "MILES", "SEATS", "CREWS", "STORM", "SUNNY", "WORLD",
  "SHORE", "RIVER", "HILLS", "NORTH", "SOUTH", "LOCAL", "CHECK", "EARLY", "LUNCH", "SNACK",
];

export const wordleBeforePlayDefaults = () => {
  const t = WORDLE_SDK_THEME;
  return {
    thumbnail_image: GAME_THUMBNAILS.wordle,
    title: "Wordle",
    title_color: t.fg,
    background_color: t.bg,
    text_color: t.fg,
    tile_color: t.surface,
    tile_border_color: t.line,
    letter_color: t.fg,
    correct_color: t.accent,
    correct_text_color: t.accentFg,
    close_color: t.alt,
    close_text_color: t.fg,
    absent_color: t.line,
    absent_text_color: t.muted,
    key_color: t.surface,
    key_text_color: t.fg,
    action_key_color: t.fg,
    action_key_text_color: t.bg,
    bonus_guess_1: "100",
    bonus_guess_2: "100",
    bonus_guess_3: "50",
    bonus_guess_4: "25",
    bonus_guess_5: "0",
    bonus_guess_6: "0",
    hint_enabled: "on",
    hint_text: "Hint: letter {position} is {letter}",
    hint_color: t.alt,
    hint_text_color: t.fg,
    streak_card_color: t.surface,
    streak_border_color: t.line,
    streak_text_color: t.muted,
    streak_done_color: t.accent,
    streak_done_text_color: t.accentFg,
    streak_upcoming_color: t.bg,
    streak_text: "{streak}-day streak",
    today_text: "Solve today: +{coins} coins",
    header_pill_text: "Day {day} streak",
    streak_rule_text: "Coins grow daily. Miss a day and the streak resets to 0.",
    too_short_text: "Not enough letters",
    not_a_word_text: "Not in word list",
    read_instructions:
      "<p>How to play</p><ol><li>Guess the five-letter word.</li><li>Dark tiles are in the right place; light tiles are in the word but in the wrong place.</li><li>Solve it to grow your streak. Fewer guesses pay more.</li></ol>",
    terms_and_conditions:
      "<p>Terms &amp; conditions</p><ol><li>One word per day; the day changes at midnight IST.</li><li>Missing a day or failing the word resets the streak.</li><li>Brand reserves the right to modify or withdraw the offer.</li></ol>",
  };
};

export const wordleRewardBeforePlayDefaults = (reward) => ({ before_play_reward: reward });

export const wordleRewardAfterPlayDefaults = (reward) => {
  const t = WORDLE_SDK_THEME;
  if (reward?.id === "bl_next_time") {
    return { title: "Not today", title_colour: t.fg, description: "The word was {answer}. Your streak starts again tomorrow.", description_color: t.muted };
  }
  return {
    sheet_background_color: t.surface,
    context_text: "Solved in {guesses} · {streak}-day streak",
    context_color: t.fg,
    logo_image: JACKPOT_SDK_ICONS.coins,
    card_background_color: t.accent,
    card_label_color: t.accentFg,
    reward_value_color: t.accentFg,
    title: `${reward?.name || "Reward"} credited`,
    title_colour: t.fg,
    description: "Day {streak} streak bonus. Come back tomorrow for +{next} coins.",
    description_color: t.muted,
    validity_text: "Expires in 30 days",
    pill_background_color: t.bg,
    pill_text_color: t.fg,
    tnc_text: "Terms & conditions",
    tnc_color: t.muted,
    show_cta: true,
    start_shopping_cta_text: "Use it now",
    start_shopping_link: "https://www.goindigo.in/",
    button_background_color: t.fg,
    button_text_color: t.surface,
    go_back_cta_text: "Saved to My Rewards",
    go_back_color: t.muted,
  };
};

// Pulls words out of pasted text or an uploaded file: anything between letters separates words.
// Returns the good list and what was dropped, so the panel can say why.
export const parseWordleWords = (text) => {
  const tokens = String(text || "").toUpperCase().split(/[^A-Z]+/).filter(Boolean);
  const words = [];
  const seen = new Set();
  const wrongLength = [];
  let repeated = 0;
  tokens.forEach((token) => {
    if (token.length !== WORDLE_LENGTH) wrongLength.push(token);
    else if (seen.has(token)) repeated += 1;
    else {
      seen.add(token);
      words.push(token);
    }
  });
  return { words, wrongLength, repeated };
};

// The same two passes as the server (wordle.utils.ts colorGuess), so previews colour repeated letters correctly
export const wordleColors = (guess, answer) => {
  const left = answer.split("");
  const colors = guess.split("").map((letter, i) => {
    if (left[i] !== letter) return "miss";
    left[i] = null;
    return "hit";
  });
  guess.split("").forEach((letter, i) => {
    if (colors[i] === "hit") return;
    const at = left.indexOf(letter);
    if (at >= 0) {
      colors[i] = "near";
      left[at] = null;
    }
  });
  return colors;
};

// A bonus field is only shown for a guess the player can actually make
export const wordleHidesField = (backendLabel, beforePlay) => {
  const match = /^bonus_guess_(\d)$/.exec(backendLabel || "");
  const attempts = parseInt(beforePlay?.attempts?.id ?? beforePlay?.attempts, 10) || 6;
  return !!match && parseInt(match[1], 10) > attempts;
};

// What each streak day pays: the winning reward's value x the day (the SDK's 10 to 70 until a value is set)
export const wordleLadder = (rewards) => {
  const winner = (Array.isArray(rewards) ? rewards : []).find((r) => r?.reward_type?.id && r.reward_type.id !== "bl_next_time");
  const base = parseFloat(winner?.reward_value ?? winner?.FIXED_CASHBACK_reward_value) || 10;
  return Array.from({ length: WORDLE_STREAK_CYCLE }, (_, i) => base * (i + 1));
};
